import React, { useMemo, useState } from 'react'
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import { Add, Create, Delete, Edit, Send, Undo } from '@material-ui/icons'
import SearchIcon from '@material-ui/icons/Search'

import {
    EligibleLease,
    RenewalOffer,
    RenewalOfferStatus,
} from '../../../models/Concession'
import {
    useEligibleLeases,
    useRenewalOffers,
} from '../../../hooks/useRenewalOffers'
import { useRenewalSequences } from '../../../hooks/useRenewalSequences'
import { useLeads } from '../../../hooks/Outreach/useLeads'
import { useUser } from '../../../hooks/useUser'
import { displayApiDate } from '../../Concessions/utils'
import { formatCurrency, renewalStatusLabel } from '../../RenewalOffers/utils'
import { CreateOffersModal } from '../../RenewalOffers/CreateOffersModal'
import { EditOfferModal } from '../../RenewalOffers/EditOfferModal'
import { SendOffersDialog } from '../../RenewalOffers/SendOffersDialog'
import { NextActionTableIcon } from '../components/NextActionMark'
import { usePersonFile } from '../PersonFile/PersonFileContext'
import { StageSwitcher } from '../LeadsPipeline/components/StageSwitcher'
import {
    LOST_STATUS_FILTERS,
    RENEWAL_BOARD_TABS,
    RenewalsBoardStage,
    SIGNING_SUBSTATUS_FILTERS,
    SigningSubstatus,
    eligibleMatchesQuery,
    offerBoardStage,
    offerMatchesQuery,
    resolveEligibleNextAction,
    resolveRenewalNextAction,
    signingSubstatus,
    signingSubstatusLabel,
} from './renewalsBoard'
import { RenewalsFunnel } from './RenewalsFunnel'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    header: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'stretch',
        gap: theme.spacing(1.5),
    },
    tiles: {
        display: 'flex',
        flexWrap: 'wrap',
        alignContent: 'flex-start',
        gap: theme.spacing(1),
        flex: '1 1 420px',
    },
    tile: {
        minWidth: 140,
        padding: theme.spacing(1.25, 1.5),
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'border-color 120ms ease, box-shadow 120ms ease',
        '&:hover': {
            borderColor: theme.palette.primary.light,
        },
    },
    tileActive: {
        borderColor: theme.palette.primary.main,
        boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
        backgroundColor:
            theme.palette.type === 'dark'
                ? 'rgba(25, 118, 210, 0.12)'
                : 'rgba(25, 118, 210, 0.06)',
    },
    tileValue: {
        fontSize: '1.25rem',
        fontWeight: 700,
        fontFamily: 'monospace',
        lineHeight: 1.2,
        color: theme.palette.text.primary,
    },
    tileLabel: {
        fontSize: '0.875rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
        marginTop: 2,
    },
    tileHint: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginTop: 2,
    },
    tools: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    search: {
        flex: '1 1 220px',
        minWidth: 220,
    },
    statusFilters: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(0.75),
    },
    actionBar: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
    },
    tableCard: {
        position: 'relative',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        overflow: 'hidden',
    },
    tableHead: {
        backgroundColor: theme.palette.grey[50],
        '& .MuiTableCell-head': {
            fontSize: '0.6875rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: theme.palette.text.secondary,
            whiteSpace: 'nowrap',
        },
    },
    clickableRow: {
        cursor: 'pointer',
    },
    nameCell: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        minWidth: 0,
    },
    personName: {
        fontWeight: 600,
    },
    personNameClickable: {
        '&:hover': {
            color: theme.palette.primary.main,
            textDecoration: 'underline',
        },
    },
    actionBtn: {
        textTransform: 'none',
        fontWeight: 600,
        whiteSpace: 'nowrap',
    },
}))

const STATUS_TONE: Record<RenewalOfferStatus, { bg: string; fg: string }> = {
    draft: { bg: '#f3f4f6', fg: '#6b7280' },
    offered: { bg: '#6570E620', fg: '#6570E6' },
    accepted: { bg: '#d1fae5', fg: '#047857' },
    declined: { bg: '#ffe4e6', fg: '#be123c' },
    transfer_requested: { bg: '#fef3c7', fg: '#b45309' },
    expired: { bg: '#f3f4f6', fg: '#6b7280' },
    revoked: { bg: '#ffe4e6', fg: '#be123c' },
}

const SIGNING_TONE: Record<SigningSubstatus, { bg: string; fg: string }> = {
    packet_not_sent: { bg: '#f3f4f6', fg: '#6b7280' },
    send_failed: { bg: '#ffe4e6', fg: '#be123c' },
    waiting_on_resident: { bg: '#fef3c7', fg: '#b45309' },
    awaiting_pm: { bg: '#fef3c7', fg: '#b45309' },
}

interface OffersTabProps {
    readOnly: boolean
    leasingSeasonId?: number
}

export const OffersTab: React.FC<OffersTabProps> = ({
    readOnly,
    leasingSeasonId,
}) => {
    const classes = useStyles()
    const { workspaceUser } = useUser()
    const apartmentId = workspaceUser?.active_workspace?.id
    const {
        offers,
        isLoading,
        sendOffer,
        bulkSend,
        revokeOffer,
        bulkRevoke,
        deleteOffer,
        changeSequence,
        sendLeasePackage,
        refetch,
    } = useRenewalOffers({ leasingSeasonId, kind: 'renewal' })
    const { eligibleLeases, isLoading: eligibleLoading } = useEligibleLeases(
        true,
    )
    const { sequences } = useRenewalSequences()
    const {
        sendLeasePackage: sendLeadLeasePackage,
        signLeasePackageAsProperty,
        isSendingLeasePackage: isSendingLeadPacket,
        isSigningLeasePackageAsProperty,
    } = useLeads({
        apartmentId,
        enabled: false,
    })

    const [stage, setStage] = useState<RenewalsBoardStage>('eligible')
    const [query, setQuery] = useState('')
    const [signingFilter, setSigningFilter] = useState<
        SigningSubstatus | 'all'
    >('all')
    const [lostFilter, setLostFilter] = useState<
        typeof LOST_STATUS_FILTERS[number]
    >('all')
    const [createOpen, setCreateOpen] = useState(false)
    const [editOffer, setEditOffer] = useState<RenewalOffer | undefined>()
    const [selected, setSelected] = useState<Set<number>>(new Set())
    const [actingLeadId, setActingLeadId] = useState<number | null>(null)
    const { openPersonFile } = usePersonFile()
    const [sendDialogTarget, setSendDialogTarget] = useState<{
        mode: 'single' | 'bulk'
        offerId?: number
        tenantName?: string
        offerIds?: number[]
    } | null>(null)
    const [confirmDialog, setConfirmDialog] = useState<{
        title: string
        body: string
        confirmLabel: string
        variant: 'delete' | 'revoke'
        onConfirm: () => void
    } | null>(null)

    const stageOffers = useMemo(() => {
        return offers.filter((offer) => offerBoardStage(offer) === stage)
    }, [offers, stage])

    const counts = useMemo(() => {
        const next: Record<RenewalsBoardStage, number> = {
            eligible: eligibleLeases.length,
            draft: 0,
            offered: 0,
            transfer: 0,
            signing: 0,
            future_resident: 0,
            lost: 0,
        }
        for (const offer of offers) {
            next[offerBoardStage(offer)] += 1
        }
        return next
    }, [offers, eligibleLeases.length])

    const searchCounts = useMemo(() => {
        if (!query.trim()) return null
        const next: Record<string, number> = {
            eligible: eligibleLeases.filter((row) =>
                eligibleMatchesQuery(row, query),
            ).length,
            draft: 0,
            offered: 0,
            transfer: 0,
            signing: 0,
            future_resident: 0,
            lost: 0,
        }
        for (const offer of offers) {
            if (offerMatchesQuery(offer, query)) {
                next[offerBoardStage(offer)] += 1
            }
        }
        return next
    }, [offers, eligibleLeases, query])

    const filteredEligible = useMemo(
        () => eligibleLeases.filter((row) => eligibleMatchesQuery(row, query)),
        [eligibleLeases, query],
    )

    const filteredOffers = useMemo(() => {
        return stageOffers.filter((offer) => {
            if (!offerMatchesQuery(offer, query)) return false
            if (stage === 'signing' && signingFilter !== 'all') {
                return signingSubstatus(offer) === signingFilter
            }
            if (stage === 'lost' && lostFilter !== 'all') {
                return offer.status === lostFilter
            }
            return true
        })
    }, [stageOffers, query, stage, signingFilter, lostFilter])

    const draftOffers = useMemo(
        () => filteredOffers.filter((offer) => offer.status === 'draft'),
        [filteredOffers],
    )
    const offeredOffers = useMemo(
        () => filteredOffers.filter((offer) => offer.status === 'offered'),
        [filteredOffers],
    )
    const selectableOffers = useMemo(() => [...draftOffers, ...offeredOffers], [
        draftOffers,
        offeredOffers,
    ])
    const selectedDraftIds = useMemo(
        () =>
            Array.from(selected).filter((id) =>
                draftOffers.some((offer) => offer.id === id),
            ),
        [selected, draftOffers],
    )
    const selectedOfferedIds = useMemo(
        () =>
            Array.from(selected).filter((id) =>
                offeredOffers.some((offer) => offer.id === id),
            ),
        [selected, offeredOffers],
    )

    const needsSignatureCount = counts.signing
    const holesCount = counts.lost
    const loading = isLoading || eligibleLoading

    const handleStageChange = (next: string) => {
        setStage(next as RenewalsBoardStage)
        setSelected(new Set())
        setSigningFilter('all')
        setLostFilter('all')
    }

    const openPerson = (offer: RenewalOffer) => {
        openPersonFile({
            leadId: offer.lead,
            userId: offer.tenant,
            renewalOffer: offer,
            sequences,
            pathTabs: RENEWAL_BOARD_TABS,
            pathStage: offerBoardStage(offer),
            onChangeSequence: !readOnly
                ? (offerId, sequence) =>
                      changeSequence.mutate({
                          id: offerId,
                          sequence,
                      })
                : undefined,
            isChangingSequence: changeSequence.isLoading,
        })
    }

    const toggleSelect = (id: number) => {
        setSelected((prev) => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    const toggleAll = () => {
        if (selected.size > 0 && selected.size === selectableOffers.length) {
            setSelected(new Set())
        } else {
            setSelected(new Set(selectableOffers.map((offer) => offer.id)))
        }
    }

    const handleSignAsPm = async (leadId: number) => {
        setActingLeadId(leadId)
        try {
            await signLeasePackageAsProperty(leadId)
            refetch()
        } finally {
            setActingLeadId(null)
        }
    }

    const handleSendPacket = (offer: RenewalOffer) => {
        sendLeasePackage.mutate(offer.id)
    }

    const showingCount =
        stage === 'eligible' ? filteredEligible.length : filteredOffers.length

    const tiles: Array<{
        id: RenewalsBoardStage
        label: string
        hint: string
        value: number
    }> = [
        {
            id: 'eligible',
            label: 'Eligible',
            hint: 'No offer yet',
            value: counts.eligible,
        },
        {
            id: 'offered',
            label: 'Offered',
            hint: 'Waiting on response',
            value: counts.offered,
        },
        {
            id: 'transfer',
            label: 'Transfer',
            hint: 'Wants a different floor plan',
            value: counts.transfer,
        },
        {
            id: 'signing',
            label: 'Needs signature',
            hint: 'Accepted, not signed',
            value: needsSignatureCount,
        },
        {
            id: 'future_resident',
            label: 'Signed',
            hint: 'Lease fully signed',
            value: counts.future_resident,
        },
        {
            id: 'lost',
            label: 'Holes',
            hint: 'Declined or lost',
            value: holesCount,
        },
    ]

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Box className={classes.tiles}>
                    {tiles.map((tile) => (
                        <Box
                            key={tile.id}
                            className={`${classes.tile} ${
                                stage === tile.id ? classes.tileActive : ''
                            }`}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleStageChange(tile.id)}
                            onKeyDown={(event) => {
                                if (
                                    event.key === 'Enter' ||
                                    event.key === ' '
                                ) {
                                    event.preventDefault()
                                    handleStageChange(tile.id)
                                }
                            }}
                        >
                            <Typography className={classes.tileValue}>
                                {loading ? '—' : tile.value}
                            </Typography>
                            <Typography className={classes.tileLabel}>
                                {tile.label}
                            </Typography>
                            <Typography className={classes.tileHint}>
                                {tile.hint}
                            </Typography>
                        </Box>
                    ))}
                </Box>
                <RenewalsFunnel
                    counts={counts}
                    searchCounts={searchCounts}
                    activeStage={stage}
                    loading={loading}
                    onStageChange={handleStageChange}
                />
            </Box>

            <StageSwitcher
                tabs={RENEWAL_BOARD_TABS}
                activeTabId={stage}
                counts={counts}
                searchMatches={searchCounts}
                isSearchActive={Boolean(query.trim())}
                onTabChange={handleStageChange}
            />

            <Box className={classes.tools}>
                <TextField
                    className={classes.search}
                    size="small"
                    variant="outlined"
                    placeholder="Search by name, email, or unit"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" color="disabled" />
                            </InputAdornment>
                        ),
                    }}
                />
                {stage === 'signing' && (
                    <Box className={classes.statusFilters}>
                        {SIGNING_SUBSTATUS_FILTERS.map((filter) => (
                            <Chip
                                key={filter.value}
                                label={filter.label}
                                size="small"
                                clickable
                                color={
                                    signingFilter === filter.value
                                        ? 'primary'
                                        : 'default'
                                }
                                onClick={() => setSigningFilter(filter.value)}
                            />
                        ))}
                    </Box>
                )}
                {stage === 'lost' && (
                    <Box className={classes.statusFilters}>
                        {LOST_STATUS_FILTERS.map((filter) => (
                            <Chip
                                key={filter}
                                label={
                                    filter === 'all'
                                        ? 'All'
                                        : renewalStatusLabel(filter)
                                }
                                size="small"
                                clickable
                                color={
                                    lostFilter === filter
                                        ? 'primary'
                                        : 'default'
                                }
                                onClick={() => setLostFilter(filter)}
                            />
                        ))}
                    </Box>
                )}
            </Box>

            {!readOnly &&
                (stage === 'eligible' ||
                    stage === 'draft' ||
                    stage === 'offered') && (
                    <Box className={classes.actionBar}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<Add />}
                            onClick={() => setCreateOpen(true)}
                            style={{ textTransform: 'none' }}
                        >
                            New Offers
                        </Button>
                        {selectedDraftIds.length > 0 && (
                            <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                startIcon={<Send />}
                                onClick={() =>
                                    setSendDialogTarget({
                                        mode: 'bulk',
                                        offerIds: selectedDraftIds,
                                    })
                                }
                                disabled={bulkSend.isLoading}
                                style={{ textTransform: 'none' }}
                            >
                                Send {selectedDraftIds.length} draft
                                {selectedDraftIds.length === 1 ? '' : 's'}
                            </Button>
                        )}
                        {selectedOfferedIds.length > 0 && (
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Undo />}
                                onClick={() =>
                                    setConfirmDialog({
                                        title: 'Revoke offers',
                                        body: `Revoke ${
                                            selectedOfferedIds.length
                                        } offered offer${
                                            selectedOfferedIds.length === 1
                                                ? ''
                                                : 's'
                                        }? Tenants will no longer be able to accept them.`,
                                        confirmLabel: `Revoke ${
                                            selectedOfferedIds.length
                                        } offer${
                                            selectedOfferedIds.length === 1
                                                ? ''
                                                : 's'
                                        }`,
                                        variant: 'revoke',
                                        onConfirm: () => {
                                            bulkRevoke.mutate(
                                                selectedOfferedIds,
                                                {
                                                    onSuccess: () =>
                                                        setSelected(new Set()),
                                                },
                                            )
                                        },
                                    })
                                }
                                disabled={bulkRevoke.isLoading}
                                style={{
                                    textTransform: 'none',
                                    color: '#b45309',
                                    borderColor: '#b45309',
                                }}
                            >
                                Revoke {selectedOfferedIds.length} offer
                                {selectedOfferedIds.length === 1 ? '' : 's'}
                            </Button>
                        )}
                        {selectableOffers.length > 0 && (
                            <>
                                <Checkbox
                                    checked={
                                        selected.size > 0 &&
                                        selected.size ===
                                            selectableOffers.length
                                    }
                                    indeterminate={
                                        selected.size > 0 &&
                                        selected.size < selectableOffers.length
                                    }
                                    onChange={toggleAll}
                                    size="small"
                                    color="primary"
                                />
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                >
                                    {selected.size > 0
                                        ? `${selected.size} selected`
                                        : 'Select drafts or offered'}
                                </Typography>
                            </>
                        )}
                    </Box>
                )}

            <Box className={classes.tableCard}>
                {loading ? (
                    <Box display="flex" justifyContent="center" py={6}>
                        <CircularProgress size={28} />
                    </Box>
                ) : stage === 'eligible' ? (
                    <EligibleTable
                        classes={classes}
                        rows={filteredEligible}
                        empty={
                            eligibleLeases.length === 0
                                ? 'No eligible residents right now.'
                                : 'No residents match this search.'
                        }
                    />
                ) : (
                    <OffersTable
                        classes={classes}
                        stage={stage}
                        rows={filteredOffers}
                        empty={
                            offers.length === 0
                                ? 'No renewal offers yet. Create offers to get started.'
                                : 'No offers match this filter.'
                        }
                        readOnly={readOnly}
                        selected={selected}
                        actingLeadId={actingLeadId}
                        sendingPacket={sendLeasePackage.isLoading}
                        signingAsPm={isSigningLeasePackageAsProperty}
                        onToggleSelect={toggleSelect}
                        onOpenPerson={openPerson}
                        onEdit={setEditOffer}
                        onSend={(offer) =>
                            setSendDialogTarget({
                                mode: 'single',
                                offerId: offer.id,
                                tenantName: offer.tenant_name,
                            })
                        }
                        onDelete={(offer) =>
                            setConfirmDialog({
                                title: 'Delete offer',
                                body: `Permanently delete the offer for ${offer.tenant_name}? This action cannot be undone.`,
                                confirmLabel: 'Delete offer',
                                variant: 'delete',
                                onConfirm: () => deleteOffer.mutate(offer.id),
                            })
                        }
                        onRevoke={(offer) =>
                            setConfirmDialog({
                                title: 'Revoke offer',
                                body: `Revoke the renewal offer to ${offer.tenant_name}? The tenant will no longer be able to accept it.`,
                                confirmLabel: 'Revoke offer',
                                variant: 'revoke',
                                onConfirm: () => revokeOffer.mutate(offer.id),
                            })
                        }
                        onSendPacket={handleSendPacket}
                        onSignAsPm={handleSignAsPm}
                    />
                )}
            </Box>
            <Typography variant="caption" color="textSecondary">
                Showing {showingCount}
                {stage === 'eligible'
                    ? ` eligible resident${showingCount === 1 ? '' : 's'}`
                    : ` offer${showingCount === 1 ? '' : 's'}`}
            </Typography>

            <CreateOffersModal
                open={createOpen}
                handleClose={() => setCreateOpen(false)}
                leasingSeasonId={leasingSeasonId}
            />
            <EditOfferModal
                offer={editOffer}
                handleClose={() => setEditOffer(undefined)}
            />
            <ConfirmDialog
                dialog={confirmDialog}
                onClose={() => setConfirmDialog(null)}
            />
            <SendOffersDialog
                open={sendDialogTarget !== null}
                offerCount={
                    sendDialogTarget?.mode === 'bulk'
                        ? sendDialogTarget.offerIds?.length ?? 0
                        : 1
                }
                tenantName={sendDialogTarget?.tenantName}
                sequences={sequences}
                isLoading={sendOffer.isLoading || bulkSend.isLoading}
                onConfirm={(sequence) => {
                    if (!sendDialogTarget) return
                    if (
                        sendDialogTarget.mode === 'single' &&
                        sendDialogTarget.offerId
                    ) {
                        sendOffer.mutate(
                            { id: sendDialogTarget.offerId, sequence },
                            { onSuccess: () => setSendDialogTarget(null) },
                        )
                    } else if (
                        sendDialogTarget.mode === 'bulk' &&
                        sendDialogTarget.offerIds
                    ) {
                        bulkSend.mutate(
                            {
                                offerIds: sendDialogTarget.offerIds,
                                sequence,
                            },
                            {
                                onSuccess: () => {
                                    setSendDialogTarget(null)
                                    setSelected(new Set())
                                },
                            },
                        )
                    }
                }}
                onClose={() => setSendDialogTarget(null)}
            />
        </Box>
    )
}

function EligibleTable({
    classes,
    rows,
    empty,
}: {
    classes: ReturnType<typeof useStyles>
    rows: EligibleLease[]
    empty: string
}) {
    return (
        <TableContainer>
            <Table size="small">
                <TableHead className={classes.tableHead}>
                    <TableRow>
                        <TableCell>Tenant</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Unit</TableCell>
                        <TableCell>Lease end</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.length === 0 ? (
                        <EmptyRow colSpan={4} message={empty} />
                    ) : (
                        rows.map((lease) => (
                            <TableRow key={lease.id} hover>
                                <TableCell>
                                    <Box className={classes.nameCell}>
                                        <NextActionTableIcon
                                            action={resolveEligibleNextAction()}
                                        />
                                        <Typography
                                            variant="body2"
                                            style={{ fontWeight: 600 }}
                                        >
                                            {lease.tenant_name}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                    >
                                        {lease.tenant_email}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="caption">
                                        {lease.unit_name} ·{' '}
                                        {lease.area_config_name}{' '}
                                        {lease.area_label}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="caption"
                                        style={{ fontFamily: 'monospace' }}
                                    >
                                        {displayApiDate(lease.end_date)}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

function OffersTable({
    classes,
    stage,
    rows,
    empty,
    readOnly,
    selected,
    actingLeadId,
    sendingPacket,
    signingAsPm,
    onToggleSelect,
    onOpenPerson,
    onEdit,
    onSend,
    onDelete,
    onRevoke,
    onSendPacket,
    onSignAsPm,
}: {
    classes: ReturnType<typeof useStyles>
    stage: RenewalsBoardStage
    rows: RenewalOffer[]
    empty: string
    readOnly: boolean
    selected: Set<number>
    actingLeadId: number | null
    sendingPacket: boolean
    signingAsPm: boolean
    onToggleSelect: (id: number) => void
    onOpenPerson: (offer: RenewalOffer) => void
    onEdit: (offer: RenewalOffer) => void
    onSend: (offer: RenewalOffer) => void
    onDelete: (offer: RenewalOffer) => void
    onRevoke: (offer: RenewalOffer) => void
    onSendPacket: (offer: RenewalOffer) => void
    onSignAsPm: (leadId: number) => void
}) {
    const showSelect = !readOnly && (stage === 'draft' || stage === 'offered')
    const showLease = stage === 'signing' || stage === 'future_resident'
    const showOfferCols = stage === 'draft' || stage === 'offered'
    const showTransfer = stage === 'transfer'
    const colSpan =
        3 +
        (showSelect ? 1 : 0) +
        (showOfferCols ? 4 : 0) +
        (showLease ? 1 : 0) +
        (showTransfer ? 1 : 0) +
        (stage === 'lost' ? 1 : 0) +
        (readOnly ? 0 : 1)

    return (
        <TableContainer>
            <Table size="small">
                <TableHead className={classes.tableHead}>
                    <TableRow>
                        {showSelect && (
                            <TableCell
                                padding="checkbox"
                                style={{ width: 40 }}
                            />
                        )}
                        <TableCell>Tenant</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Unit</TableCell>
                        {showOfferCols && (
                            <>
                                <TableCell>Proposed rent</TableCell>
                                <TableCell>Term</TableCell>
                                <TableCell>Start</TableCell>
                                <TableCell>Concession</TableCell>
                            </>
                        )}
                        {showLease && <TableCell>Lease</TableCell>}
                        {showTransfer && (
                            <TableCell>Desired floor plan</TableCell>
                        )}
                        {stage === 'lost' && <TableCell>Status</TableCell>}
                        {!readOnly && (
                            <TableCell align="right">Actions</TableCell>
                        )}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.length === 0 ? (
                        <EmptyRow colSpan={colSpan} message={empty} />
                    ) : (
                        rows.map((offer) => {
                            const isDraft = offer.status === 'draft'
                            const isOffered = offer.status === 'offered'
                            const canSelect =
                                showSelect && (isDraft || isOffered)
                            const sub =
                                offer.status === 'accepted'
                                    ? signingSubstatus(offer)
                                    : null
                            const leaseTone = sub
                                ? SIGNING_TONE[sub]
                                : { bg: '#d1fae5', fg: '#047857' }
                            const clickable = true

                            return (
                                <TableRow
                                    key={offer.id}
                                    hover
                                    className={
                                        clickable ? classes.clickableRow : ''
                                    }
                                    onClick={() => {
                                        if (clickable) onOpenPerson(offer)
                                    }}
                                >
                                    {showSelect && (
                                        <TableCell padding="checkbox">
                                            {canSelect ? (
                                                <Checkbox
                                                    checked={selected.has(
                                                        offer.id,
                                                    )}
                                                    onChange={() =>
                                                        onToggleSelect(offer.id)
                                                    }
                                                    size="small"
                                                    color="primary"
                                                    onClick={(event) =>
                                                        event.stopPropagation()
                                                    }
                                                />
                                            ) : (
                                                <span />
                                            )}
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        <Box className={classes.nameCell}>
                                            <NextActionTableIcon
                                                action={resolveRenewalNextAction(
                                                    offer,
                                                    stage,
                                                )}
                                            />
                                            <Typography
                                                variant="body2"
                                                className={`${
                                                    classes.personName
                                                } ${
                                                    clickable
                                                        ? classes.personNameClickable
                                                        : ''
                                                }`}
                                            >
                                                {offer.tenant_name}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                        >
                                            {offer.tenant_email}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">
                                            {offer.unit_name} ·{' '}
                                            {offer.area_config_name}{' '}
                                            {offer.area_label}
                                        </Typography>
                                    </TableCell>
                                    {showOfferCols && (
                                        <>
                                            <TableCell>
                                                <Typography
                                                    variant="caption"
                                                    style={{
                                                        fontFamily: 'monospace',
                                                        color: '#6570E6',
                                                    }}
                                                >
                                                    {formatCurrency(
                                                        offer.proposed_rent,
                                                    )}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="caption"
                                                    style={{
                                                        fontFamily: 'monospace',
                                                    }}
                                                >
                                                    {offer.proposed_term_months}{' '}
                                                    mo
                                                    {offer.proposed_term_months ===
                                                    1
                                                        ? ''
                                                        : 's'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="caption"
                                                    style={{
                                                        fontFamily: 'monospace',
                                                    }}
                                                >
                                                    {displayApiDate(
                                                        offer.proposed_start_date,
                                                    )}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {offer.concession_names
                                                    ?.length > 0 ? (
                                                    <Box
                                                        display="flex"
                                                        flexWrap="wrap"
                                                        style={{ gap: 4 }}
                                                    >
                                                        {offer.concession_names.map(
                                                            (name, index) => (
                                                                <Chip
                                                                    key={index}
                                                                    label={name}
                                                                    size="small"
                                                                    style={{
                                                                        backgroundColor:
                                                                            '#d1fae5',
                                                                        color:
                                                                            '#047857',
                                                                        fontWeight: 600,
                                                                        fontSize:
                                                                            '0.625rem',
                                                                        height: 20,
                                                                        borderRadius: 6,
                                                                    }}
                                                                />
                                                            ),
                                                        )}
                                                    </Box>
                                                ) : (
                                                    <Typography variant="caption">
                                                        —
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        </>
                                    )}
                                    {showLease && (
                                        <TableCell>
                                            <Chip
                                                label={
                                                    stage === 'future_resident'
                                                        ? 'Signed'
                                                        : signingSubstatusLabel(
                                                              sub ??
                                                                  'packet_not_sent',
                                                          )
                                                }
                                                size="small"
                                                style={{
                                                    backgroundColor:
                                                        leaseTone.bg,
                                                    color: leaseTone.fg,
                                                    fontWeight: 600,
                                                    fontSize: '0.625rem',
                                                    height: 22,
                                                }}
                                            />
                                        </TableCell>
                                    )}
                                    {showTransfer && (
                                        <TableCell>
                                            <Typography variant="caption">
                                                {offer.transfer_desired_unit_config_name ||
                                                    '—'}
                                            </Typography>
                                        </TableCell>
                                    )}
                                    {stage === 'lost' && (
                                        <TableCell>
                                            <Chip
                                                label={renewalStatusLabel(
                                                    offer.status,
                                                )}
                                                size="small"
                                                style={{
                                                    backgroundColor:
                                                        STATUS_TONE[
                                                            offer.status
                                                        ].bg,
                                                    color:
                                                        STATUS_TONE[
                                                            offer.status
                                                        ].fg,
                                                    fontWeight: 600,
                                                    fontSize: '0.625rem',
                                                    height: 22,
                                                }}
                                            />
                                        </TableCell>
                                    )}
                                    {!readOnly && (
                                        <TableCell
                                            align="right"
                                            onClick={(event) =>
                                                event.stopPropagation()
                                            }
                                        >
                                            <Box
                                                display="flex"
                                                justifyContent="flex-end"
                                                alignItems="center"
                                                gridGap={4}
                                            >
                                                {isDraft && (
                                                    <>
                                                        <Tooltip title="Edit terms">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    onEdit(
                                                                        offer,
                                                                    )
                                                                }
                                                            >
                                                                <Edit fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Send to tenant">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    onSend(
                                                                        offer,
                                                                    )
                                                                }
                                                            >
                                                                <Send
                                                                    fontSize="small"
                                                                    color="primary"
                                                                />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    onDelete(
                                                                        offer,
                                                                    )
                                                                }
                                                            >
                                                                <Delete
                                                                    fontSize="small"
                                                                    color="error"
                                                                />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                )}
                                                {isOffered && (
                                                    <Tooltip title="Revoke offer">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                onRevoke(offer)
                                                            }
                                                        >
                                                            <Undo
                                                                fontSize="small"
                                                                color="secondary"
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {stage === 'signing' &&
                                                    sub === 'awaiting_pm' &&
                                                    offer.lead != null && (
                                                        <Tooltip
                                                            title={
                                                                signingAsPm &&
                                                                actingLeadId ===
                                                                    offer.lead
                                                                    ? 'Opening…'
                                                                    : 'Sign as PM'
                                                            }
                                                        >
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    color="primary"
                                                                    disabled={
                                                                        signingAsPm &&
                                                                        actingLeadId ===
                                                                            offer.lead
                                                                    }
                                                                    onClick={() =>
                                                                        onSignAsPm(
                                                                            offer.lead!,
                                                                        )
                                                                    }
                                                                >
                                                                    <Create fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                {stage === 'signing' &&
                                                    (sub ===
                                                        'packet_not_sent' ||
                                                        sub ===
                                                            'send_failed') && (
                                                        <Tooltip
                                                            title={
                                                                sendingPacket
                                                                    ? 'Sending…'
                                                                    : sub ===
                                                                      'send_failed'
                                                                    ? 'Resend packet'
                                                                    : 'Send packet'
                                                            }
                                                        >
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    color="primary"
                                                                    disabled={
                                                                        sendingPacket
                                                                    }
                                                                    onClick={() =>
                                                                        onSendPacket(
                                                                            offer,
                                                                        )
                                                                    }
                                                                >
                                                                    <Send fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                            </Box>
                                        </TableCell>
                                    )}
                                </TableRow>
                            )
                        })
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

function EmptyRow({ colSpan, message }: { colSpan: number; message: string }) {
    return (
        <TableRow>
            <TableCell colSpan={colSpan} align="center">
                <Box py={4}>
                    <Typography variant="body2" color="textSecondary">
                        {message}
                    </Typography>
                </Box>
            </TableCell>
        </TableRow>
    )
}

function ConfirmDialog({
    dialog,
    onClose,
}: {
    dialog: {
        title: string
        body: string
        confirmLabel: string
        variant: 'delete' | 'revoke'
        onConfirm: () => void
    } | null
    onClose: () => void
}) {
    return (
        <Dialog
            open={dialog !== null}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{ style: { borderRadius: 12, padding: 8 } }}
        >
            {dialog && (
                <>
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        pt={3}
                        pb={1}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            style={{
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                backgroundColor:
                                    dialog.variant === 'delete'
                                        ? '#E2563B15'
                                        : '#b4530915',
                            }}
                        >
                            {dialog.variant === 'delete' ? (
                                <Delete style={{ color: '#E2563B' }} />
                            ) : (
                                <Undo style={{ color: '#b45309' }} />
                            )}
                        </Box>
                    </Box>
                    <DialogTitle
                        disableTypography
                        style={{ textAlign: 'center', paddingBottom: 0 }}
                    >
                        <Typography
                            variant="h6"
                            style={{ fontWeight: 600, fontSize: '1.1rem' }}
                        >
                            {dialog.title}
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ textAlign: 'center', lineHeight: 1.6 }}
                        >
                            {dialog.body}
                        </Typography>
                    </DialogContent>
                    <DialogActions style={{ padding: '8px 24px 20px', gap: 8 }}>
                        <Button
                            onClick={onClose}
                            variant="outlined"
                            fullWidth
                            style={{
                                textTransform: 'none',
                                borderRadius: 8,
                                fontWeight: 600,
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={() => {
                                dialog.onConfirm()
                                onClose()
                            }}
                            style={{
                                textTransform: 'none',
                                borderRadius: 8,
                                fontWeight: 600,
                                color: '#fff',
                                backgroundColor:
                                    dialog.variant === 'delete'
                                        ? '#E2563B'
                                        : '#b45309',
                                boxShadow: 'none',
                            }}
                        >
                            {dialog.confirmLabel}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    )
}
